import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Prose } from "@/components/site/PageHeader";
import { EditablePageShell } from "@/components/site/EditablePageShell";
import { pageQueryOptions } from "@/lib/pages.functions";
import { acceptAll, getConsent, rejectAll, setConsent, subscribeConsent } from "@/lib/consent";

const DESC =
  "How Glow Long Golf uses essential, analytics, and marketing cookies — and how to manage your preferences at any time.";

export const Route = createFileRoute("/legal/cookies")({
  loader: ({ context }) => context.queryClient.ensureQueryData(pageQueryOptions("legal/cookies")),
  head: ({ loaderData }) => ({
    meta: [
      { title: loaderData?.title || "Cookie Policy — Glow Long Golf" },
      { name: "description", content: loaderData?.meta_description || DESC },
      { property: "og:title", content: loaderData?.title || "Cookie Policy — Glow Long Golf" },
      { property: "og:description", content: loaderData?.meta_description || DESC },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://glowlonggolf.com/legal/cookies" },
    ],
    links: [{ rel: "canonical", href: "https://glowlonggolf.com/legal/cookies" }],
  }),
  errorComponent: ({ error }) => <div className="p-8 text-sm text-destructive" role="alert">{error.message}</div>,
  notFoundComponent: () => <div className="p-8 text-sm text-muted-foreground">Not found.</div>,
  component: CookiePolicyPage,
});

function CookiePolicyPage() {
  return (
    <EditablePageShell slug="legal/cookies" defaults={{ eyebrow: "Legal", title: "Cookie Policy" }}>
      <Prose>
        <p><strong>Last Updated: August 22, 2026</strong></p>
        <p>
          Glow Long Golf, LLC (“Glow Long Golf,” “Glow Long,” “we,” “us,” or “our”) uses cookies and
          similar technologies on glowlonggolf.com to operate the website, support shopping and
          account features, understand how the site is used, and, where permitted, support marketing
          and advertising.
        </p>
        <p>
          This Cookie Policy explains the types of cookies and similar technologies we may use and
          the choices available to you.
        </p>

        <h2>What Are Cookies?</h2>
        <p>
          Cookies are small text files stored on your browser or device when you visit a website. We
          may also use similar technologies, such as pixels, local storage, tags, and other
          identifiers that help websites function, remember preferences, measure activity, or
          support advertising.
        </p>

        <h2>Types of Cookies We Use</h2>
        <p>We currently organize cookies and similar technologies into three general categories:</p>

        <h3>Essential Cookies</h3>
        <p>
          Essential cookies are required for core website functions and cannot generally be disabled
          through our cookie preference tools.
        </p>
        <p>They may be used to:</p>
        <ul>
          <li>keep items in your shopping cart;</li>
          <li>operate checkout;</li>
          <li>maintain login and account sessions;</li>
          <li>remember basic site preferences;</li>
          <li>process security and fraud-prevention functions;</li>
          <li>support payment and transaction functionality; and</li>
          <li>maintain the technical operation and security of the website.</li>
        </ul>
        <p>
          Because these cookies are necessary to provide services you request, they may be used
          without optional cookie consent where permitted by law.
        </p>

        <h3>Analytics Cookies</h3>
        <p>Analytics cookies help us understand how visitors use glowlonggolf.com.</p>
        <p>They may help us measure information such as:</p>
        <ul>
          <li>pages viewed;</li>
          <li>time spent on the site;</li>
          <li>traffic sources;</li>
          <li>navigation patterns;</li>
          <li>device and browser information;</li>
          <li>interactions with website features; and</li>
          <li>technical performance or errors.</li>
        </ul>
        <p>
          We use this information to understand site performance and improve the customer
          experience. Analytics cookies may be provided by Glow Long Golf or by third-party
          analytics or technology providers. Where consent is required by applicable law, analytics
          cookies will only be used after you provide consent.
        </p>

        <h3>Marketing Cookies</h3>
        <p>
          Marketing cookies and similar technologies may be used to support advertising, measure
          marketing performance, or help us understand whether visitors interact with our
          advertisements.
        </p>
        <p>
          These technologies may be provided by third-party advertising or social-media platforms
          and may collect information about your browser, device, interactions with the site, or
          activity across websites or services.
        </p>
        <p>
          Where required by applicable law,{" "}
          <strong>marketing cookies will only be activated after you choose to accept them.</strong>
        </p>
        <p>We do not use marketing cookies to collect or store complete payment-card information.</p>

        <h2>Third-Party Technologies</h2>
        <p>
          Some cookies or similar technologies may be placed or operated by third parties that
          provide services to Glow Long Golf. These may include providers that support:
        </p>
        <ul>
          <li>website hosting and ecommerce functionality;</li>
          <li>payment processing;</li>
          <li>analytics;</li>
          <li>fraud prevention and security;</li>
          <li>advertising;</li>
          <li>social media;</li>
          <li>email marketing; or</li>
          <li>other website services.</li>
        </ul>
        <p>
          These third parties may process information according to their own privacy policies and
          legal obligations.
        </p>
        <p>
          For more information about how personal information may be collected, used, or disclosed,
          please review our <a href="/legal/privacy">Privacy Policy</a>.
        </p>

        <h2>Your Cookie Choices</h2>
        <p>When you visit glowlonggolf.com, you may be given the option to:</p>
        <ul>
          <li>Accept All cookies;</li>
          <li>Reject Non-Essential cookies; or</li>
          <li>manage your cookie preferences by category.</li>
        </ul>
        <p>
          Rejecting non-essential cookies will not prevent you from using the core shopping
          functions of the site, although certain optional features, analytics, or personalized
          marketing functions may not operate. Essential cookies will remain active because they are
          necessary for the website to function.
        </p>

        <h2>Changing Your Preferences</h2>
        <p>
          You may change your cookie preferences at any time using the cookie settings below or the
          preference controls available on the website. You can also control or delete cookies
          through your browser settings.
        </p>
        <p>
          Please note that disabling cookies through your browser may affect certain website
          functions, including shopping-cart, account, checkout, or login features.
        </p>

        <CookiePreferences />

        <h2>Global Privacy Control and Other Privacy Signals</h2>
        <p>
          Some browsers and extensions allow users to send privacy preference signals, including
          Global Privacy Control (“GPC”). Where applicable law requires us to recognize a legally
          valid privacy preference signal, we will process that signal as required.
        </p>

        <h2>Cookies and Personal Information</h2>
        <p>
          Information collected through cookies and similar technologies may be considered personal
          information under certain privacy laws. Our handling of personal information is described
          in our <a href="/legal/privacy">Privacy Policy</a>.
        </p>
        <p>
          Glow Long Golf does not sell customer names, email addresses, or customer lists for
          monetary consideration.
        </p>
        <p>
          If our use of advertising or tracking technologies changes in a way that creates additional
          privacy rights or opt-out obligations, we will update our practices and provide the choices
          required by applicable law.
        </p>

        <h2>Changes to This Cookie Policy</h2>
        <p>We may update this Cookie Policy from time to time to reflect changes in:</p>
        <ul>
          <li>the technologies used on our website;</li>
          <li>our service providers;</li>
          <li>our advertising or analytics practices; or</li>
          <li>applicable legal requirements.</li>
        </ul>
        <p>
          The current version will be posted on this page with the revised “Last Updated” date.
        </p>

        <h2>Contact Us</h2>
        <p>
          If you have questions about our use of cookies or privacy practices, please contact us
          through the <a href="/contact">Contact</a> page at glowlonggolf.com or email:{" "}
          <a href="mailto:info@glowlonggolf.com">info@glowlonggolf.com</a>
        </p>
        <p>Glow Long Golf, LLC</p>
      </Prose>
    </EditablePageShell>
  );
}

/** Live cookie preference controls (category-level manage + accept/reject). */
function CookiePreferences() {
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const sync = () => {
      const c = getConsent();
      setAnalytics(c.analytics);
      setMarketing(c.marketing);
    };
    sync();
    return subscribeConsent(sync);
  }, []);

  return (
    <div className="not-prose my-8 rounded-2xl border border-border bg-card p-5">
      <h3 className="font-display text-lg font-semibold text-foreground">Cookie settings</h3>
      <div className="mt-3 grid gap-2 text-sm text-foreground">
        <label className="flex items-center gap-2 opacity-60">
          <input type="checkbox" checked readOnly /> Essential (always active)
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={analytics}
            onChange={(e) => {
              setAnalytics(e.target.checked);
              setSaved(false);
            }}
          />{" "}
          Analytics
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={marketing}
            onChange={(e) => {
              setMarketing(e.target.checked);
              setSaved(false);
            }}
          />{" "}
          Marketing
        </label>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          onClick={() => {
            acceptAll();
            setSaved(true);
          }}
          className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
        >
          Accept
        </button>
        <button
          onClick={() => {
            rejectAll();
            setSaved(true);
          }}
          className="rounded-full border border-border bg-card px-4 py-2 text-sm"
        >
          Reject
        </button>
        <button
          onClick={() => {
            setConsent({ analytics, marketing });
            setSaved(true);
          }}
          className="rounded-full border border-border bg-card px-4 py-2 text-sm"
        >
          Save preferences
        </button>
      </div>
      {saved && <p className="mt-3 text-xs text-muted-foreground">Your cookie preferences have been saved.</p>}
    </div>
  );
}
